/**
 * Life4Earth V2 — Resource ↔ Need match scoring (DEV-1148)
 *
 * Pure, side-effect-free scoring of a user resource (supply) against a
 * community need (demand). Replaces the old flat word-overlap matching that
 * lived inline in ResourcesPage / CommunityNeeds.
 *
 * Score = category fit + keyword overlap + proximity + need priority, capped
 * at MAX_TOTAL, then bucketed into strong / partial / low.
 *
 * Design notes:
 *   - The weights in DEFAULT_WEIGHTS encode a product value judgment
 *     ("category match dominates everything else"). They are intentionally
 *     centralized and overridable (pass `weights` in opts) so they can be
 *     tuned in one place without touching call sites. See DEV-1148.
 *   - PROXIMITY is supplied by the caller as community-hierarchy *hops*
 *     (0 = same community, 1 = parent/child, …). The community-needs feed
 *     knows this from its maxHops fetch; the need record itself does NOT
 *     carry a distance, so proximity contributes 0 when hops are unknown.
 *     We do NOT invent a distance.
 *   - SUPPLY HAS NO QUANTITY. User resources don't carry a numeric quantity
 *     (deprecated field), while needs do. Scoring therefore can't reward
 *     "fully fills the quantity" — that's deferred pending the supply-quantity
 *     decision (DEV-1152). Keep that out of this module.
 */

import type { PreflightPriority } from '../PreFlight/preflightTypes';
import { normalizeCategory } from '../PreFlight/preflightTypes';

/* ------------------------------------------------------------------ */
/*  Inputs — narrow structural shapes                                  */
/*  (UserResource and CommunityNeedItem both satisfy these)            */
/* ------------------------------------------------------------------ */

/** Supply side. `UserResource` is assignable to this. */
export interface MatchResource {
  category: string;
  label: string;
  description?: string | null;
}

/** Demand side. `CommunityNeedItem` is assignable to this. */
export interface MatchNeed {
  category: string;
  label: string;
  description?: string | null;
  /** 'critical' | 'important' | 'nice_to_have' (or unknown → no bonus). */
  priority?: string | null;
  /** Optional rich labels that broaden keyword matching. */
  descriptor_tags?: string[];
}

/* ------------------------------------------------------------------ */
/*  Buckets & proximity tiers                                          */
/* ------------------------------------------------------------------ */

export type MatchBucket = 'strong' | 'partial' | 'low';

/**
 * Proximity tier derived from community-hierarchy hop distance.
 *   town   = same community (0 hops)
 *   county = one hop (direct parent/child)
 *   region = two hops
 *   beyond = three or more hops (no proximity bonus)
 */
export type ProximityTier = 'town' | 'county' | 'region' | 'beyond';

/** Map a hop distance to a proximity tier. `null`/`undefined` → unknown. */
export function hopsToTier(hops: number | null | undefined): ProximityTier | null {
  if (hops == null || hops < 0) return null;
  if (hops === 0) return 'town';
  if (hops === 1) return 'county';
  if (hops === 2) return 'region';
  return 'beyond';
}

/* ------------------------------------------------------------------ */
/*  Weights (the tunable product value judgment)                       */
/* ------------------------------------------------------------------ */

export interface MatchWeights {
  /** Points when resource and need share the same category. */
  category: number;
  /** Points per distinct shared keyword. */
  keywordEach: number;
  /** Max points contributable by keyword overlap. */
  keywordCap: number;
  /** Points by proximity tier. `beyond` and unknown contribute 0. */
  proximity: Record<ProximityTier, number>;
  /** Points by the need's stated priority. */
  priority: Record<PreflightPriority, number>;
  /** total ≥ this → 'strong'. */
  strongThreshold: number;
  /** total ≥ this (and < strong) → 'partial'; else 'low'. */
  partialThreshold: number;
  /** Score ceiling. */
  maxTotal: number;
}

export const DEFAULT_WEIGHTS: MatchWeights = Object.freeze({
  category: 60,
  keywordEach: 14,
  keywordCap: 28,
  proximity: { town: 15, county: 8, region: 3, beyond: 0 },
  priority: { critical: 10, important: 5, nice_to_have: 0 },
  strongThreshold: 60,
  partialThreshold: 32,
  maxTotal: 100,
});

/* ------------------------------------------------------------------ */
/*  Result                                                             */
/* ------------------------------------------------------------------ */

export interface MatchScore {
  total: number;
  bucket: MatchBucket;
  /** Per-factor contributions, for tooltips / debugging / "why this match". */
  parts: {
    category: number;
    keyword: number;
    proximity: number;
    priority: number;
  };
  /** Distinct keywords that overlapped (for explainability). */
  sharedKeywords: string[];
  /** Resolved proximity tier, or null when hops weren't supplied. */
  proximityTier: ProximityTier | null;
}

/* ------------------------------------------------------------------ */
/*  Tokenization                                                       */
/* ------------------------------------------------------------------ */

const STOPWORDS = new Set([
  'the', 'and', 'for', 'our', 'your', 'any', 'some', 'with', 'from', 'this',
  'that', 'are', 'was', 'will', 'can', 'need', 'needs', 'needed', 'help',
  'looking', 'seeking', 'want', 'wanted', 'please', 'available', 'offer',
  'one', 'two', 'few', 'per', 'use', 'used', 'using', 'into', 'out', 'about',
]);

/**
 * Lowercase, strip punctuation, drop short/stopword tokens, and apply a light
 * plural stem so "saplings" matches "sapling" and "volunteers" matches
 * "volunteer". Returns DISTINCT tokens.
 */
export function tokenize(text: string | null | undefined): string[] {
  if (!text) return [];
  const raw = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const out = new Set<string>();
  for (const t of raw) {
    if (t.length < 3 || STOPWORDS.has(t)) continue;
    // light plural stem: drop trailing 's' on tokens longer than 3 chars
    const stem = t.length > 3 && t.endsWith('s') ? t.slice(0, -1) : t;
    out.add(stem);
  }
  return [...out];
}

/* ------------------------------------------------------------------ */
/*  Scoring                                                            */
/* ------------------------------------------------------------------ */

export interface ScoreOptions {
  /** Community-hierarchy hop distance from the user to the need's community. */
  proximityHops?: number | null;
  /** Override the default weights (partial overrides are merged). */
  weights?: Partial<MatchWeights>;
}

function resolveWeights(override?: Partial<MatchWeights>): MatchWeights {
  if (!override) return DEFAULT_WEIGHTS;
  return {
    ...DEFAULT_WEIGHTS,
    ...override,
    proximity: { ...DEFAULT_WEIGHTS.proximity, ...(override.proximity ?? {}) },
    priority: { ...DEFAULT_WEIGHTS.priority, ...(override.priority ?? {}) },
  };
}

function priorityPoints(weights: MatchWeights, priority: string | null | undefined): number {
  if (!priority) return 0;
  const key = priority as PreflightPriority;
  return weights.priority[key] ?? 0;
}

/**
 * Score a single resource against a single need. Pure — same inputs always
 * produce the same output.
 */
export function scoreResourceAgainstNeed(
  resource: MatchResource,
  need: MatchNeed,
  opts: ScoreOptions = {}
): MatchScore {
  const w = resolveWeights(opts.weights);

  // Category — the dominant signal. Lowercase/trim first: the DB enum is
  // lowercase (migration 029) but be defensive against casing drift, since
  // normalizeCategory() treats an unrecognized value (e.g. 'EQUIPMENT') as
  // 'other' rather than folding case.
  const normCat = (c: string) => normalizeCategory((c ?? '').toLowerCase().trim());
  const sameCategory = normCat(resource.category) === normCat(need.category);
  const categoryPts = sameCategory ? w.category : 0;

  // Keyword overlap — distinct shared tokens across label/description (+ need tags).
  const resourceTokens = new Set(tokenize(`${resource.label} ${resource.description ?? ''}`));
  const needTokens = new Set(
    tokenize(`${need.label} ${need.description ?? ''} ${(need.descriptor_tags ?? []).join(' ')}`)
  );
  const sharedKeywords = [...resourceTokens].filter((t) => needTokens.has(t));
  const keywordPts = Math.min(sharedKeywords.length * w.keywordEach, w.keywordCap);

  // Proximity — only when the caller supplied hops.
  const proximityTier = hopsToTier(opts.proximityHops);
  const proximityPts = proximityTier ? w.proximity[proximityTier] : 0;

  // Need priority.
  const priorityPts = priorityPoints(w, need.priority);

  const total = Math.min(categoryPts + keywordPts + proximityPts + priorityPts, w.maxTotal);

  return {
    total,
    bucket: bucketFor(total, w),
    parts: { category: categoryPts, keyword: keywordPts, proximity: proximityPts, priority: priorityPts },
    sharedKeywords,
    proximityTier,
  };
}

/** Bucket a raw total. */
export function bucketFor(total: number, weights: MatchWeights = DEFAULT_WEIGHTS): MatchBucket {
  if (total >= weights.strongThreshold) return 'strong';
  if (total >= weights.partialThreshold) return 'partial';
  return 'low';
}

/* ------------------------------------------------------------------ */
/*  Ranking helper (consumed by the match feed — DEV-1147)             */
/* ------------------------------------------------------------------ */

export interface RankedNeed<N extends MatchNeed> {
  need: N;
  score: MatchScore;
}

/**
 * Score every need against the resource and return them sorted best-first.
 * `hopsFor` lets the caller supply per-need proximity (e.g. derived from the
 * community-hierarchy walk); omit it to score without proximity.
 */
export function rankNeedsForResource<N extends MatchNeed>(
  resource: MatchResource,
  needs: readonly N[],
  opts: { weights?: Partial<MatchWeights>; hopsFor?: (need: N) => number | null | undefined } = {}
): RankedNeed<N>[] {
  return needs
    .map((need) => ({
      need,
      score: scoreResourceAgainstNeed(resource, need, {
        weights: opts.weights,
        proximityHops: opts.hopsFor ? opts.hopsFor(need) : null,
      }),
    }))
    .sort((a, b) => b.score.total - a.score.total);
}
